import React from "react";
import { FaGithub } from "react-icons/fa6";
import { FiArrowUpRight } from "react-icons/fi";
import Reveal from "./Reveal";
import SectionHeading from "./SectionHeading";
import { projects } from "../data/projects";

const featured = projects.filter((project) => project.tagline);
const others = projects.filter((project) => !project.tagline);

const pad = (n) => String(n).padStart(2, "0");

const hostOf = (url) => {
    try {
        return new URL(url).hostname.replace(/^www\./, "");
    } catch (e) {
        return url;
    }
};

// Optimised WebP renditions live in /public/img; the original PNG stays as the fallback.
const webpSrcSet = (src) => {
    const name = src.replace(/^\//, "").replace(/\.png$/i, "");
    return `/img/${name}-800.webp 800w, /img/${name}-1600.webp 1600w`;
};

const Screenshot = ({ project, sizes, className = "" }) => (
    <picture>
        <source type="image/webp" srcSet={webpSrcSet(project.image)} sizes={sizes} />
        <img
            src={project.image}
            alt={`Screenshot of ${project.title}`}
            loading="lazy"
            decoding="async"
            className={`h-full w-full object-cover object-top transition-transform duration-1200 ease-out-expo ${className}`}
        />
    </picture>
);

const ProjectLinks = ({ project, compact = false }) => {
    const sameDestination = project.liveLink === project.githubLink;
    return (
        <div className="flex shrink-0 flex-wrap items-center gap-2">
            {!sameDestination && (
                <a href={project.liveLink} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">
                    <span>Live site</span>
                    <FiArrowUpRight className="btn-arrow" aria-hidden="true" />
                    <span className="sr-only">: {project.title} (opens in a new tab)</span>
                </a>
            )}
            <a
                href={project.githubLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`btn btn-ghost btn-sm ${compact ? "h-[38px] w-[38px] !px-0" : ""}`}
            >
                <FaGithub aria-hidden="true" />
                <span className={compact ? "sr-only" : ""}>GitHub</span>
                <span className="sr-only">: {project.title} (opens in a new tab)</span>
            </a>
        </div>
    );
};

const FeaturedProject = ({ project, delay }) => (
    <Reveal as="article" delay={delay} className="flex flex-col">
        <a href={project.liveLink} target="_blank" rel="noopener noreferrer" tabIndex={-1} className="group block">
            <div className="flex items-center justify-between gap-4 border border-b-0 border-fg/10 bg-surface px-4 py-2.5">
                <span className="flex items-center gap-1.5" aria-hidden="true">
                    <span className="h-2 w-2 rounded-full bg-fg/15" />
                    <span className="h-2 w-2 rounded-full bg-fg/15" />
                    <span className="h-2 w-2 rounded-full bg-accent" />
                </span>
                <span className="label truncate normal-case tracking-[0.06em]">{hostOf(project.liveLink)}</span>
                <FiArrowUpRight
                    aria-hidden="true"
                    className="shrink-0 text-muted transition-transform duration-500 ease-out-expo group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-accent-text"
                />
            </div>
            <div className="relative aspect-[16/10] overflow-hidden border border-fg/10 bg-surface">
                <Screenshot project={project} sizes="(min-width: 768px) 40vw, 100vw" className="group-hover:scale-[1.04]" />
            </div>
        </a>
        <div className="mt-5 flex items-baseline justify-between gap-4">
            <h3 className="text-[2.1rem] font-extrabold leading-none tracking-[-0.01em] stretch-condensed">{project.title}</h3>
            <span className="label shrink-0 tabular-nums">{project.period}</span>
        </div>
        <p className="mt-2 font-medium text-accent-text">{project.tagline}</p>
        <p className="mt-3 flex-1 text-[15px] leading-relaxed text-muted text-pretty">{project.description}</p>
        <ul className="mt-5 flex flex-wrap gap-1.5" aria-label="Technologies">
            {project.stack.map((tech) => (
                <li key={tech} className="rounded-full border border-fg/15 px-2.5 py-1 font-mono text-[11px] text-muted">
                    {tech}
                </li>
            ))}
        </ul>
        <div className="mt-5">
            <ProjectLinks project={project} />
        </div>
    </Reveal>
);

const ProjectRow = ({ project, delay }) => (
    <Reveal as="li" delay={delay}>
        <article className="group grid grid-cols-[5.5rem_minmax(0,1fr)] items-start gap-x-5 gap-y-4 border-t border-fg/10 py-5 sm:grid-cols-[8.5rem_minmax(0,1fr)_auto] sm:items-center">
            <a
                href={project.liveLink}
                target="_blank"
                rel="noopener noreferrer"
                tabIndex={-1}
                className="block aspect-[16/10] overflow-hidden border border-fg/10 bg-surface"
            >
                <Screenshot project={project} sizes="9rem" className="group-hover:scale-[1.08]" />
            </a>
            <div className="min-w-0">
                <h3 className="text-[1.4rem] font-bold leading-tight tracking-[-0.01em] transition-colors duration-300 stretch-semi group-hover:text-accent-text">
                    {project.title}
                </h3>
                <p className="mt-1 text-[14px] leading-relaxed text-muted text-pretty">{project.description}</p>
                <p className="mt-2 font-mono text-[11px] text-subtle">{project.stack.join(" · ")}</p>
            </div>
            <div className="col-start-2 sm:col-start-3">
                <ProjectLinks project={project} compact />
            </div>
        </article>
    </Reveal>
);

const Projects = () => (
    <section id="projects" aria-labelledby="projects-title" className="py-10 md:py-14">
        <SectionHeading
            id="projects-title"
            index="02"
            label="Work"
            title="Featured"
            accent="Projects"
            aside={<p className="label">{pad(projects.length)} projects</p>}
        />

        <div className="mt-8 grid gap-x-8 gap-y-14 md:mt-10 md:grid-cols-2">
            {featured.map((project, index) => (
                <FeaturedProject key={project.title} project={project} delay={index * 120} />
            ))}
        </div>

        <Reveal variant="fade">
            <h3 className="label mt-14 text-fg">More projects</h3>
        </Reveal>
        <ul className="mt-5 border-b border-fg/10">
            {others.map((project, index) => (
                <ProjectRow key={project.title} project={project} delay={index * 60} />
            ))}
        </ul>
    </section>
);

export default Projects;
